// Fuente única de los planes de suscripción (datos + helpers). Antes vivía
// inline en `Checkout.tsx`; se extrajo para compartirla con el alta enterprise
// (`EnterpriseCheckout.tsx`) sin duplicar precios/features. Ver ADR.
//
// El techo de video NO se escribe a mano: sale de `segundosDeVideoPorCreditos` sobre el
// `max_fotos_mes` del propio plan, a 2 créditos por segundo. Se habla de segundos y no
// de videos porque un video no tiene largo fijo (ADR-2026-09-24-planes-en-segundos-de-video).
import { segundosDeVideoPorCreditos } from "../components/pricing/pricingContent";
import {
  CLASSIC_STUDIO_PATH,
  V2_STUDIO_PATH,
  entryStudioPath,
} from "./userGroups";
import { ONBOARDING_V2 } from "../components/onboarding/onboardingV2Routes";
import { onboardingPendiente } from "./onboardingPendiente";

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number; // precio en ARS (Argentina); otros países se resuelven aparte
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const plans: SubscriptionPlan[] = [
  {
    id: "emprendimiento",
    name: "Básico",
    price: 21999,
    description:
      "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
    features: [
      "Hasta 60 imágenes por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "~Generación de videos con IA",
    ],
    max_fotos_mes: 60,
  },
  {
    id: "negocio",
    name: "Pro",
    price: 69999,
    description:
      "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
    features: [
      "Hasta 250 imágenes por mes",
      `Hasta ${segundosDeVideoPorCreditos(250).toLocaleString("es-AR")} segundos de video por mes`,
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte prioritario",
    ],
    max_fotos_mes: 250,
  },
  {
    id: "enterprise",
    name: "Ultra",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 900 imágenes por mes",
      `Hasta ${segundosDeVideoPorCreditos(900).toLocaleString("es-AR")} segundos de video por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 900,
  },
  {
    id: "enterprise1",
    name: "Ultra Nivel 1",
    price: 109999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 500 imágenes por mes",
      `Hasta ${segundosDeVideoPorCreditos(500).toLocaleString("es-AR")} segundos de video por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 500,
  },
  {
    id: "enterprise2",
    name: "Ultra Nivel 2",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 1.000 imágenes por mes",
      `Hasta ${segundosDeVideoPorCreditos(1000).toLocaleString("es-AR")} segundos de video por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 1000,
  },
  {
    id: "enterprise3",
    name: "Ultra Nivel 3",
    price: 399999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 2.000 imágenes por mes",
      `Hasta ${segundosDeVideoPorCreditos(2000).toLocaleString("es-AR")} segundos de video por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 2000,
  },
];

export function getPlanById(id: string): SubscriptionPlan | undefined {
  return plans.find((p) => p.id === id);
}

/**
 * Imágenes que SUMA un cambio de plan (upgrade o downgrade) sobre las restantes
 * actuales. Espejo del `upgradeIncrement` del payment server
 * (`atlas-payment-server/src/domain/plans.ts`, PLAN_CATALOG). En la renovación NO
 * se suma: refill a `max_fotos_mes`.
 *
 * **Se DERIVA de la cuota del plan, ya no es una tabla a mano.** El server sacó los
 * bonus de créditos: un plan da su cuota y nada más, así que el incremento de un
 * cambio de plan ES `max_fotos_mes` por definición. La tabla escrita a mano tenía
 * `negocio: 300` (250 + 50 de bonus) y `enterprise: 1100` (900 + 200), que es lo que
 * el checkout le prometía al cliente —"se sumarán 300 imágenes"— cuando el server ya
 * iba a sumar 250. Derivarlo del catálogo hace que esa diferencia no pueda volver a
 * abrirse sola. Ver ADR-2026-08-19-incremento-de-upgrade-sale-de-la-cuota.
 *
 * Si el server vuelve a separar incremento de cuota, este es el lugar donde hay que
 * volver a escribirlos a mano — y entonces sí, uno por uno.
 */
export const PLAN_UPGRADE_INCREMENTS: Record<string, number> = Object.fromEntries(
  plans.map((p) => [p.id, p.max_fotos_mes]),
);

/**
 * ¿El plan incluye la videollamada de capacitación? Pro y todos los Ultra
 * (ADR-2026-10-06-la-llamada-de-capacitacion-tambien-es-de-pro). Es lo que decide la
 * pantalla post-pago `/capacitacion-ultra` y el aviso de la home para agendarla.
 */
export function incluyeLlamadaDeCapacitacion(planId: string | null | undefined): boolean {
  return !!planId && (planId === "negocio" || esPlanUltra(planId));
}

/** El nombre corto del plan para el copy de la llamada ("Pro", "Ultra"); `null` si no la incluye. */
export function nombreDelPlanConLlamada(planId: string | null | undefined): "Pro" | "Ultra" | null {
  if (!incluyeLlamadaDeCapacitacion(planId)) return null;
  return planId === "negocio" ? "Pro" : "Ultra";
}

/** ¿El id es alguno de los planes Ultra (incluye el id legacy sin nivel)? */
export function esPlanUltra(planId: string): boolean {
  return (
    planId === "enterprise" ||
    planId === "enterprise1" ||
    planId === "enterprise2" ||
    planId === "enterprise3"
  );
}

/**
 * ¿Este cambio de plan admite el cupón de descuento (hoy el "Hot Sale" del 30%
 * del primer mes)? Es la ÚNICA fuente de la regla: la consumen el catálogo
 * (`buildPlanViews`, lo que el cliente ve) y el checkout (`Checkout.tsx`, lo que
 * realmente se cobra), que antes tenían la condición duplicada a mano.
 *
 * Reglas, en orden:
 *
 * 1. **El Básico nunca lleva promo** — decisión comercial de siempre: su precio
 *    ya es el de entrada.
 * 2. **Altas de quien NUNCA pagó**: el descuento sigue vigente para Pro y Ultra.
 *    Es la promo de captación (links de la landing, banner de upsell del home) y
 *    no se tocó.
 * 3. **Clientes que ya pagan**: el descuento quedó SOLO en Básico → Pro
 *    (ADR-2026-08-18-descuento-de-upgrade-solo-basico-a-pro). Antes lo llevaba
 *    cualquier upgrade — Básico → Ultra, Pro → Ultra y hasta un cambio de nivel
 *    dentro de Ultra —, o sea que el 30% se regalaba justo en los cambios de
 *    mayor ticket.
 * 4. **Bajadas de plan**: sin descuento, como siempre (no entran en ninguna de
 *    las ramas de arriba).
 *
 * `yaFueClientePago` (`company_profiles.paid_plan_start_date is not null`) existe
 * porque dar de baja deja el plan en `gratis`, y sin ese dato una cuenta que ya
 * pagó es **indistinguible** de una nueva: cancelar, esperar el fin del ciclo y
 * volver a darse de alta devolvía el 30% en cualquier plan — o sea, un camino más
 * barato que el upgrade que la regla (3) acababa de encarecer. Se cierra del todo:
 * quien pagó alguna vez no vuelve a ver la promo de captación
 * (ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis).
 *
 * `currentPlan` vacío se trata como `gratis`. Cualquier otro plan de origen que no
 * sea `emprendimiento` (planes a medida, `experto`, ids futuros) cae en "sin
 * descuento" a propósito — ante la duda con algo que COBRA, precio de lista. Por
 * eso `yaFueClientePago` es un parámetro OBLIGATORIO y no uno con default: el
 * default seguro sería `true` (sin descuento), y un default silencioso en la
 * dirección cómoda es exactamente cómo se regala plata sin que nadie lo note.
 */
export function cambioDePlanAdmiteDescuento(
  currentPlan: string | null | undefined,
  targetPlanId: string,
  yaFueClientePago: boolean,
): boolean {
  // (1) Destinos elegibles: Pro y Ultra. El Básico queda afuera siempre.
  if (targetPlanId !== "negocio" && !esPlanUltra(targetPlanId)) return false;

  // (2) Alta desde gratis: promo de captación, solo para quien nunca pagó.
  const esAltaDesdeGratis = !currentPlan || currentPlan === "gratis";
  if (esAltaDesdeGratis) return !yaFueClientePago;

  // (3) Ya paga: únicamente Básico → Pro.
  return currentPlan === "emprendimiento" && targetPlanId === "negocio";
}

/**
 * ¿Al plan ACTUAL le queda algún upgrade con descuento? Es la pregunta de las
 * superficies que prometen el cupón sin un destino fijo — hoy la barra de
 * créditos ("Mejorá tu plan con 30% OFF"): para el gratis que nunca pagó y para
 * el Básico existe un destino con promo; para Pro y Ultra no existe ninguno, así
 * que prometer el % ahí es prometer un precio que el checkout no cobra
 * (ADR-2026-08-31-barra-creditos-descuento-segun-plan). Derivado de
 * `cambioDePlanAdmiteDescuento` sobre el catálogo para que la regla comercial
 * siga teniendo una sola fuente.
 */
export function planAdmiteDescuentoDeUpgrade(
  currentPlan: string | null | undefined,
  yaFueClientePago: boolean,
): boolean {
  return plans.some((p) =>
    cambioDePlanAdmiteDescuento(currentPlan, p.id, yaFueClientePago),
  );
}

/**
 * Slug del link de alta enterprise → `planId`. SOLO planes que tienen `pln_` en
 * Rebill (`REBILL_PLAN_IDS`): el id base `enterprise` NO está, por eso se usan
 * los niveles. Cada plan = su propio link (ej. `/alta-empresa/ultra-1`).
 */
export const ENTERPRISE_PLAN_SLUGS: Record<string, string> = {
  basico: "emprendimiento",
  pro: "negocio",
  "ultra-1": "enterprise1",
  "ultra-2": "enterprise2",
  "ultra-3": "enterprise3",
};

/** Precio formateado en ARS (ej. `$109.999 ARS`). */
export function formatArs(amount: number): string {
  return `$${new Intl.NumberFormat("es-AR").format(amount)} ARS`;
}

// ── Precios y moneda por país ────────────────────────────────────────────
//
// Vivían como funciones locales dentro de `Checkout.tsx`. Se movieron acá para
// que el alta por link (`EnterpriseCheckout.tsx`) use EXACTAMENTE los mismos
// precios en vez de asumir ARS — que era el bug: un usuario de Chile veía
// precios argentinos y el formulario de Rebill. Ver ADR-592.
//
// El cuerpo de estas funciones se movió VERBATIM: mismos valores, mismo orden,
// mismas ramas. Lo único que cambió es que los flags de país (`isArgentina`,
// `isChile`, …) ahora se derivan del parámetro `pais` en vez de venir del
// scope del componente.

export interface PaisFlags {
  isArgentina: boolean;
  isChile: boolean;
  isColombia: boolean;
  isMexico: boolean;
  isPeru: boolean;
}

/** Normaliza el país y devuelve los flags. Misma lógica que tenía Checkout. */
export function paisFlags(pais: string | null | undefined): PaisFlags {
  const normalizedPais = (pais || "").toLowerCase();
  return {
    isArgentina: normalizedPais === "argentina",
    isChile: normalizedPais === "chile",
    isColombia: normalizedPais === "colombia",
    isMexico: normalizedPais === "méxico" || normalizedPais === "mexico",
    isPeru: normalizedPais === "perú" || normalizedPais === "peru",
  };
}

/**
 * ¿Este cobro sale por el formulario EMBEBIDO de Rebill (Argentina, in-app) o por el
 * redirect a Stripe? Son las dos únicas opciones, así que `false` = Stripe.
 *
 * Está acá —y no inline en la pantalla— porque la MISMA respuesta decide dos cosas que
 * tienen que estar de acuerdo: si se monta `<rebill-checkout>`, y si la pantalla puede
 * cambiar de layout. Cambiar de layout DESMONTA el formulario, y remontarlo le borra al
 * cliente la tarjeta a medio tipear y abre una segunda sesión de checkout contra el
 * store de módulo del SDK, que es único y compartido. Si cada una calculara el
 * predicado por su lado, la que congela y la que monta podrían decir cosas distintas.
 * Ver ADR-2026-09-22-un-solo-arbol-en-el-alta-por-link.
 *
 * Dos condiciones, las dos necesarias:
 *  · Argentina — `paisFlags` normaliza mayúsculas (la base guarda 'Argentina'; el
 *    `?pais=AR` de la landing ya pasó por `paisDesdeCodigoISO`). País vacío ⇒ `false`:
 *    sin país el precio se muestra en USD y el cobro tiene que ir por Stripe, que es el
 *    fallback correcto (ADR-592). Ante la duda con algo que cobra, el camino que NO
 *    promete ARS.
 *  · Plan del CATÁLOGO — los planes a medida cobran siempre por Stripe, sin importar el
 *    país que elija el cliente: son para el exterior por definición y Rebill ni siquiera
 *    tiene el plan cargado, así que montarles el formulario embebido sería un cobro que
 *    no existe.
 */
export function cobraConRebill(
  pais: string | null | undefined,
  esPlanDelCatalogo: boolean,
): boolean {
  return esPlanDelCatalogo && paisFlags(pais).isArgentina;
}

// Feature flag de la facturación AFIP de la SUSCRIPCIÓN. En `true` la facturación es VISIBLE para
// TODOS los usuarios de Argentina (antes quedaba OCULTA mientras AFIP estaba en homologación; solo
// los dev_testers la veían). Se prende con AFIP en PRODUCCIÓN (cert + vars en Render + AFIP_ENV=prod
// + una factura real ya emitida OK en el payment-server). Ver ADR-2026-08-13-facturacion-gate-dev-tester.
export const FACTURACION_ENABLED = true;

/** ¿Se le muestra la facturación de la suscripción a este usuario? Solo AR, y solo si el flag está
 *  prendido o es dev_tester. Único punto de verdad para gatear los accesos (Config, popup, portal). */
export function facturacionVisible(
  isArgentina: boolean,
  devTester: boolean | null | undefined,
): boolean {
  return isArgentina && (FACTURACION_ENABLED || devTester === true);
}

/** Moneda del país. Todo lo no contemplado cae a USD. */
export function currencyForPais(pais: string | null | undefined): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  return isArgentina
    ? "ARS"
    : isChile
      ? "CLP"
      : isColombia
        ? "COP"
        : isMexico
          ? "MXN"
          : isPeru
            ? "PEN"
            : "USD";
}

// ── El destino final post-pago viaja por la cadena ───────────────────────────
//
// Después de cobrar hay hasta TRES pantallas antes de entrar a la app:
// `/capacitacion-ultra` → `/facturacion-oferta` → `/facturacion`. Cada una resolvía su
// propio destino final con `entryStudioPath`, o sea el ESTUDIO. Eso alcanzaba mientras
// el destino de todos FUERA el estudio; dejó de alcanzar con el alta por link
// invertida, donde el destino es el CUESTIONARIO
// (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues).
//
// El incidente concreto: con AFIP en producción (`FACTURACION_ENABLED`) un ARGENTINO
// nunca sale por el `home` que recibe `destinoPostPago` —sale por `/facturacion-oferta`,
// o antes por `/capacitacion-ultra` si es Ultra—, así que ese `home` era código muerto
// justo para el país donde Atlas cobra. El que pagaba por el link entraba al estudio sin
// cuestionario, lo usaba, y recién lo devolvía ahí el rebote de `App.checkUserProfile`
// en la VISITA SIGUIENTE: ese rebote sólo corre en una carga completa o en un SIGNED_IN,
// y toda esta cadena son navegaciones blandas.
// Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
//
// Viaja por la URL y NO por `location.state`, por el mismo motivo por el que
// `/facturacion` ya recibe `?postPago=1`: el state se pierde al recargar, y estas
// pantallas se recargan de verdad (la de capacitación manda a Google Calendar en otra
// pestaña y en PWA standalone volver puede ser una recarga; el portal es un formulario
// largo). Sin el param, un refresh dejaba al cliente en el estudio — y peor: esas rutas
// están EXCLUIDAS del rebote de App a propósito, así que ahí no lo agarra nadie.

/** Param con el que el destino final post-pago viaja por la cadena. */
export const PARAM_HOME_POST_PAGO = 'home';

/**
 * Los ÚNICOS destinos finales que la cadena acepta. Es una whitelist y no un "cualquier
 * string que empiece con /" A PROPÓSITO: el valor llega por la URL, así que un validador
 * abierto convierte un param escrito a mano —o un state roto— en el destino de alguien
 * que acaba de pagar. Con la lista cerrada, lo peor que puede hacer un param forjado es
 * mandar a una de las tres pantallas a las que igual se podía ir.
 */
export const DESTINOS_POST_PAGO_VALIDOS: readonly string[] = [
  V2_STUDIO_PATH,
  CLASSIC_STUDIO_PATH,
  ONBOARDING_V2.setup,
];

/**
 * El destino si está en la whitelist; `null` si no —y entonces cada pantalla resuelve el
 * suyo, que es el comportamiento de siempre—. Comparación EXACTA, sin trim ni lowercase:
 * ante la duda con algo que decide adónde va el que ya pagó, no se normaliza nada.
 */
export function homePostPagoValido(
  candidato: string | null | undefined,
): string | null {
  if (!candidato) return null;
  return DESTINOS_POST_PAGO_VALIDOS.includes(candidato) ? candidato : null;
}

/**
 * `true` si ese destino es el CUESTIONARIO y no el estudio. Lo usan los botones que
 * prometían "entrar al estudio" y ya no pueden prometerlo: del cuestionario sólo se sale
 * terminándolo, así que mandarlo ahí bajo ese texto es mentirle al que acaba de pagar.
 * Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
 */
export function destinoEsElCuestionario(home: string | null | undefined): boolean {
  return homePostPagoValido(home) === ONBOARDING_V2.setup;
}

/**
 * Le cuelga el destino final a la ruta del próximo salto de la cadena. Sin destino
 * devuelve la ruta TAL CUAL: los llamadores viejos siguen navegando a la misma URL de
 * siempre, carácter por carácter. Sabe convivir con una query que ya existe
 * (`/facturacion?postPago=1`).
 */
export function rutaConHomePostPago(
  ruta: string,
  home: string | null | undefined,
): string {
  const valido = homePostPagoValido(home);
  if (!valido) return ruta;
  const separador = ruta.includes('?') ? '&' : '?';
  return `${ruta}${separador}${PARAM_HOME_POST_PAGO}=${encodeURIComponent(valido)}`;
}

/**
 * Lo que necesita cada pantalla de la cadena, en UNA llamada, porque son dos cosas
 * distintas y mezclarlas es exactamente la regresión que esto evita:
 *
 *  · `homeDelLlamador` — el destino que mandó quien cobró. Es lo ÚNICO que se propaga al
 *    siguiente salto.
 *  · `homePath` — adónde va esta pantalla cuando el usuario sale de la cadena (el botón
 *    de salida, y el guard de "entraste sin venir de un pago"). Es el del llamador si
 *    hay, y si no el que resuelve el grupo de ESTA pantalla.
 *
 * La propuesta obvia —que cada pantalla propague su `homePath`— es una REGRESIÓN real
 * sobre `/checkout`: ahí el home sale de `entryStudioPath(isDevTester, grupo)` y
 * `useUserGroup` arranca leyendo una cache de localStorage que puede estar VACÍA
 * (navegador nuevo, o después de cerrar sesión), o sea `entryStudioPath(null, null)` =
 * el estudio CLÁSICO. Congelar ese valor y pasárselo a `/facturacion-oferta` —que hoy lo
 * recalcula al montar, ya con el grupo resuelto— mandaría a un usuario de Grupo B al
 * estudio viejo. Por eso el que viaja es el del llamador, y sólo cuando es una ruta FIJA
 * que no depende del usuario.
 */
export function resolverHomePostPago(
  paramHome: string | null | undefined,
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): { homeDelLlamador: string | null; homePath: string } {
  const homeDelLlamador = homePostPagoValido(paramHome);
  return {
    homeDelLlamador,
    homePath: homeDelLlamador ?? entryStudioPath(isDevTester, grupo),
  };
}

/**
 * A dónde va el cliente después de pagar, y con qué `state`. Decide SÓLO el primer
 * salto; de ahí en más cada pantalla de la cadena sigue como siempre.
 *
 * Existe porque las dos pantallas que cobran tenían su propia copia de esta decisión y
 * se desincronizaron: `/checkout` ofrecía la facturación AFIP y `/alta-empresa` no, ni
 * siquiera le pasaba el `conFacturacion` a la pantalla de capacitación Ultra (que es el
 * flag con el que ESA encadena la oferta). O sea que todo el que pagaba por el link se
 * quedaba sin el ofrecimiento, con AFIP ya en producción.
 * Ver ADR-2026-09-22-el-destino-post-pago-es-uno-solo.
 *
 * El orden es el pedido: Pro y Ultra ven primero la llamada de capacitación incluida
 * (ADR-2026-08-25-llamada-capacitacion-ultra-post-pago) y la oferta de facturación
 * viaja decidida en `conFacturacion` para que la encadene su "Continuar".
 *
 * `home` lo pone cada pantalla, y a propósito no se unifica: `/checkout` manda al home
 * por grupo (`entryStudioPath`) y el alta por link va siempre a Interfaz V2, que es lo
 * que se pidió para ese flujo.
 */
export interface DestinoPostPago {
  ruta: string;
  /** `undefined` cuando la ruta es el home: ahí no hay estado que pasar. */
  state?: { postPago: true; conFacturacion?: boolean; plan?: string };
}

export function destinoPostPago(args: {
  /** Id del catálogo. `undefined` en un plan a medida, que no incluye la llamada. */
  planId: string | undefined;
  isArgentina: boolean;
  devTester: boolean | null | undefined;
  /**
   * Adónde va cuando no hay ninguna pantalla intermedia. En `/checkout` es el estudio
   * según grupo; en el alta por link es el CUESTIONARIO de onboarding, porque ahí la
   * empresa se creó sola para poder cobrar y todavía no se le preguntó nada
   * (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues).
   */
  home: string;
  /**
   * `true` cuando ese `home` es una ruta FIJA —que no depende del usuario ni de su
   * grupo— y tiene que SOBREVIVIR la cadena de pantallas intermedias. Hoy sólo lo manda
   * el alta por link, cuyo destino es el cuestionario: con AFIP prendida un argentino
   * nunca sale por el `return { ruta: home }` de abajo, así que sin esto el destino se
   * perdía en el primer salto.
   *
   * Default `false` = el comportamiento de siempre, y NO es un detalle: en `/checkout` el
   * `home` es `entryStudioPath(...)` resuelto en ESA pantalla, con un grupo que puede
   * venir de una cache vacía. Propagarlo le congelaría el estudio CLÁSICO a un usuario
   * de Grupo B (el porqué largo está en `resolverHomePostPago`).
   */
  homeEsFijo?: boolean;
}): DestinoPostPago {
  const { planId, isArgentina, devTester, home, homeEsFijo } = args;
  // Gate: la facturación está OCULTA para usuarios reales hasta que AFIP esté en prod
  // (FACTURACION_ENABLED); los dev_testers la ven.
  const conFacturacion = facturacionVisible(isArgentina, devTester);
  // Sólo viaja un home FIJO, y sólo si está en la whitelist. Si alguien mueve la ruta del
  // cuestionario y se olvida de `DESTINOS_POST_PAGO_VALIDOS`, esto da `null` y la cadena
  // vuelve al estudio en silencio: por eso hay un test que exige que esa ruta esté en la
  // lista.
  const homeQueViaja = homeEsFijo ? homePostPagoValido(home) : null;
  if (planId && incluyeLlamadaDeCapacitacion(planId)) {
    return {
      ruta: rutaConHomePostPago('/capacitacion-ultra', homeQueViaja),
      // El plan viaja para que la pantalla nombre el que se acaba de pagar: la cache del
      // usuario puede tener todavía el anterior.
      state: { postPago: true, conFacturacion, plan: planId },
    };
  }
  if (conFacturacion) {
    return {
      ruta: rutaConHomePostPago('/facturacion-oferta', homeQueViaja),
      state: { postPago: true },
    };
  }
  // Sin pantalla intermedia el `home` ES la ruta y no se valida: `/checkout` manda acá
  // cualquier path de estudio y ese contrato no cambia.
  return { ruta: home };
}

/**
 * A dónde sigue el "Continuar" de `/subscription/success`, la pantalla a la que vuelve el
 * que pagó por REDIRECT (Stripe, o sea el exterior). `null` = el destino de siempre.
 *
 * Es la otra punta del mismo problema, y no se puede resolver con un param: Stripe vuelve
 * SIEMPRE a la misma `success_url` global (`STRIPE_SUCCESS_URL` en
 * `atlas-payment-server/src/config.ts`), así que el que pagó por `/checkout` y el que pagó
 * por el link de alta aterrizan en la MISMA URL, sin nada del llamador. Y el rebote de
 * `App.checkUserProfile` tampoco lo agarra: `/subscription/success` está excluido a
 * propósito —si no, le roba la única pantalla que le dice que el pago salió bien— y el
 * "Continuar" es una navegación blanda.
 *
 * Entonces el destino se deduce del DATO: si la empresa todavía tiene el rubro centinela,
 * le falta el cuestionario. El rubro ya se lee en la misma consulta que el plan, así que
 * no cuesta un viaje más.
 *
 * FALLA CERRADO hacia "no interrumpir": si la fila no se pudo leer (`pudoLeerse: false`)
 * NO se manda a nadie al cuestionario. Es el mismo criterio del rebote de App, que prueba
 * el estado POSITIVO: sacar del medio a un cliente de hace un año para meterlo en un
 * cuestionario que no le corresponde es peor que llegar tarde (acá "tarde" es la próxima
 * carga completa, donde el rebote sí lo agarra).
 */
export function destinoDelExitoPorRedirect(args: {
  plan: string | null | undefined;
  rubro: string | null | undefined;
  /** `false` cuando la consulta falló o no devolvió fila: ahí no se deduce nada. */
  pudoLeerse: boolean;
}): string | null {
  const { plan, rubro, pudoLeerse } = args;
  const home = pudoLeerse && onboardingPendiente(rubro) ? ONBOARDING_V2.setup : null;
  // Pro y Ultra siguen viendo primero la llamada de capacitación incluida
  // (ADR-2026-08-25-llamada-capacitacion-ultra-post-pago,
  // ADR-2026-10-06-la-llamada-de-capacitacion-tambien-es-de-pro), con el cuestionario
  // colgado para que sobreviva ese salto.
  if (plan && incluyeLlamadaDeCapacitacion(plan)) {
    return rutaConHomePostPago('/capacitacion-ultra', home);
  }
  return home;
}

/** Precio del plan en la moneda del país. `arsPrice` es el precio base (ARS). */
export function getPlanPriceForPais(
  id: string,
  arsPrice: number,
  pais: string | null | undefined,
): number {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  if (isArgentina) return arsPrice;
  if (isChile) {
    switch (id) {
      case "emprendimiento":
        return 14999;
      case "negocio":
        return 49999;
      case "enterprise":
      case "enterprise1":
        return 79999;
      case "enterprise2":
        return 149999;
      case "enterprise3":
        return 299999;
      default:
        return 0;
    }
  }
  if (isColombia) {
    switch (id) {
      case "emprendimiento":
        return 59999;
      case "negocio":
        return 199000;
      case "enterprise":
      case "enterprise1":
        return 299999;
      case "enterprise2":
        return 549999;
      case "enterprise3":
        return 1099999;
      default:
        return 0;
    }
  }
  if (isMexico) {
    switch (id) {
      case "emprendimiento":
        return 299;
      case "negocio":
        return 999;
      case "enterprise":
      case "enterprise1":
        return 1699;
      case "enterprise2":
        return 2999;
      case "enterprise3":
        return 5999;
      default:
        return 0;
    }
  }
  if (isPeru) {
    switch (id) {
      case "emprendimiento":
        return 49;
      case "negocio":
        return 179;
      case "enterprise":
      case "enterprise1":
        return 299;
      case "enterprise2":
        return 549;
      case "enterprise3":
        return 1099;
      default:
        return 0;
    }
  }
  switch (id) {
    case "emprendimiento":
      return 15;
    case "negocio":
      return 50;
    case "enterprise":
    case "enterprise1":
      return 80;
    case "enterprise2":
      return 150;
    case "enterprise3":
      return 300;
    default:
      return 0;
  }
}

/** Precio formateado con la moneda y el locale del país. */
export function formatPriceForPais(
  amount: number,
  pais: string | null | undefined,
): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  const currencyCode = currencyForPais(pais);
  const locale = isArgentina
    ? "es-AR"
    : isChile
      ? "es-CL"
      : isColombia
        ? "es-CO"
        : isMexico
          ? "es-MX"
          : isPeru
            ? "es-PE"
            : "en-US";
  // USD: precios siempre se muestran como ".99" (valor interno entero - 0.01).
  // Resto de monedas: entero sin decimales (mantienen el comportamiento previo).
  const isUSD = currencyCode === "USD";
  const displayAmount = isUSD && amount > 0 ? amount - 0.01 : amount;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: isUSD ? 2 : 0,
    maximumFractionDigits: isUSD ? 2 : 0,
  });
  return `$${fmt.format(displayAmount)} ${currencyCode}`;
}

/**
 * Código ISO → nombre de país, en el MISMO formato que usa `CountrySelect`
 * (`components/OnboardingSelects.tsx`) y que espera `paisFlags`.
 *
 * La landing manda el país detectado como código en la URL del alta
 * (`/alta-empresa/basico?pais=CL`). Acá se traduce para poder resolver el
 * precio en moneda local desde el primer render y pre-seleccionar el país en
 * el formulario. Ver ADR-592.
 *
 * "OTHER" y cualquier código desconocido devuelven `null`: no se inventa un
 * país. Sin país, el precio cae a USD, que es el fallback correcto para todo
 * lo que no tiene tabla propia.
 */
export const PAIS_POR_CODIGO_ISO: Record<string, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  MX: 'México',
  PE: 'Perú',
};

export function paisDesdeCodigoISO(
  codigo: string | null | undefined,
): string | null {
  if (!codigo) return null;
  return PAIS_POR_CODIGO_ISO[codigo.trim().toUpperCase()] ?? null;
}

/**
 * País del checkout: el código que manda la landing en `?pais=`, y si ese no
 * sirve, el país que el cliente YA eligió a mano (guardado en `profiles.pais` /
 * `company_profiles.pais`).
 *
 * El param no alcanza solo: sale de geolocalizar la IP, y una IP puede mentir.
 * Una clienta argentina entrando desde el navegador in-app de Instagram recibió
 * `?pais=OTHER` al recargar (el proxy la geolocalizó afuera). Como el país
 * quedaba vacío, `esArgentina` daba `false` y el checkout le ofrecía Stripe en
 * USD en vez del form de Rebill en ARS. El dato declarado le gana a la IP.
 * Ver ADR-2026-08-18-alta-empresa-pais-solo-de-la-url.
 *
 * En `/alta-empresa` NO se llama directo: ahí el país sale de `resolverPantallaDelAlta`
 * (`lib/altaEmpresaPantalla.ts`), que resuelve paso y país JUNTOS para que ningún camino
 * aplique uno y se olvide del otro — que fue exactamente lo que pasó en el login
 * (ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar).
 */
export function resolverPaisCheckout(
  codigoDeLaUrl: string | null | undefined,
  paisGuardado: string | null | undefined,
): string {
  return paisDesdeCodigoISO(codigoDeLaUrl) ?? (paisGuardado?.trim() || '');
}
